import React from "react";
import {
  Drawer,
  Form,
  Input,
  Select,
  InputNumber,
  Divider,
  FormInstance,
} from "antd";
import { RawMaterial, RawMaterialStock, Supplier } from "../../../Types";
import { RawMaterialUnit, RAW_MATERIAL_UNIT_LABELS } from "../../../Enums";
import { StockBatchEditor } from "./StockBatchEditor";
import {
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../../components";

const { Option } = Select;

interface Props {
  open: boolean;
  editingMaterial: RawMaterial | null;
  form: FormInstance;
  formStocks: RawMaterialStock[];
  suppliers: Supplier[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onAddStock: () => void;
  onRemoveStock: (index: number) => void;
  onUpdateStock: (
    index: number,
    key: keyof RawMaterialStock,
    value: any,
  ) => void;
}

export const RawMaterialFormDrawer: React.FC<Props> = ({
  open,
  editingMaterial,
  form,
  formStocks,
  suppliers,
  isSubmitting,
  onClose,
  onSubmit,
  onAddStock,
  onRemoveStock,
  onUpdateStock,
}) => (
  <Drawer
    title={drawerTitle(
      editingMaterial ? "Edit Raw Material" : "Add Raw Material",
    )}
    width={DRAWER_FORM_WIDTH}
    onClose={onClose}
    open={open}
    styles={{ body: { paddingBottom: 80 } }}
    footer={drawerFooter({
      actions: [
        { label: "Cancel", onClick: onClose },
        {
          label: "Save",
          type: "primary",
          loading: isSubmitting,
          onClick: onSubmit,
        },
      ],
    })}
  >
    <Form
      form={form}
      layout="vertical"
      hideRequiredMark
      className="flex flex-col gap-1"
    >
      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="name"
          label="Material Name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="e.g. Sugar, Milk" />
        </Form.Item>

        <Form.Item
          name="code"
          label="SKU / Code"
          rules={[{ required: true, message: "Code is required" }]}
        >
          <Input placeholder="Unique code" disabled={!!editingMaterial} />
        </Form.Item>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="unit"
          label="Unit"
          rules={[{ required: true, message: "Select a unit" }]}
        >
          <Select placeholder="Select unit">
            {Object.values(RawMaterialUnit).map((unit) => (
              <Option key={unit} value={unit}>
                {RAW_MATERIAL_UNIT_LABELS[unit]}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="alert_quantity"
          label="Alert Quantity"
          rules={[{ required: true, message: "Required" }]}
          initialValue={10}
        >
          <InputNumber
            className="w-full"
            min={0}
            placeholder="Low stock threshold"
          />
        </Form.Item>
      </div>

      <Form.Item name="suppliers" label="Linked Suppliers">
        <Select
          mode="multiple"
          placeholder="Select supplier(s)"
          allowClear
          optionFilterProp="children"
        >
          {suppliers.map((s) => (
            <Option key={s.id} value={s.id}>
              {s.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Divider className="my-2" />

      <StockBatchEditor
        formStocks={formStocks}
        onAdd={onAddStock}
        onRemove={onRemoveStock}
        onUpdate={onUpdateStock}
      />
    </Form>
  </Drawer>
);
